import React from "react";
import IndustryPage from "../../components/marketing/IndustryPage";
import { Building2, MapPin, Camera, CalendarCheck, Clock, FileText, MessageSquare, Phone } from "lucide-react";

export default function RealEstate() {
  return (
    <IndustryPage
      slug="real-estate"
      industry="Real Estate"
      Icon={Building2}
      accent="#7C3AED"
      seoTitle="AI Concierge for Real Estate Agents, Brokers & Property Portals"
      seoDescription="HelloSir.ai is the AI concierge for real-estate agencies, brokers and property portals. Qualifies buyers & renters, books viewings, answers area & finance questions, and pulls listings from your live CRM 24/7 — multilingual, on chat, web voice and phone."
      seoKeywords={[
        "AI for real estate", "real estate chatbot", "AI property assistant", "AI for realtors",
        "lead qualification real estate AI", "property viewing booking AI", "AI broker assistant",
        "AI listing search", "voice AI real estate", "multilingual real estate chatbot",
        "AI for letting agents", "AI for estate agents UK",
      ]}
      hero={{
        headline: "Every lead answered. Every viewing booked. Every neighbourhood explained.",
        sub: "HelloSir.ai turns your listings into a conversation. Buyers and renters ask in plain English (or any other language), get matched to the right property and book a viewing — without you lifting a finger.",
      }}
      painPoints={[
        { Icon: Clock,        title: "Leads go cold in minutes", desc: "Most leads never get a reply within the critical first 5 minutes." },
        { Icon: Phone,        title: "Same questions, every day", desc: "School catchments, parking, council tax, pet policy — the same 20 questions on repeat." },
        { Icon: CalendarCheck, title: "Calendar tetris", desc: "Coordinating viewings across multiple agents and clients eats hours." },
      ]}
      capabilities={[
        { Icon: Building2,    title: "Match listings",        desc: "Searches your CRM by budget, beds, postcode and freeform intent (\"family home near a tube station\")." },
        { Icon: CalendarCheck, title: "Book viewings",         desc: "Reads agents' calendars in real time and confirms a slot in under 60 seconds." },
        { Icon: MapPin,       title: "Area expert",            desc: "Talks confidently about schools, transport, restaurants and council tax bands." },
        { Icon: FileText,     title: "Qualify the lead",       desc: "Captures buyer intent, budget, finance status and timeline — straight into your CRM." },
        { Icon: Camera,       title: "Send listing details",   desc: "Replies with photo carousels, floor plans and brochure PDFs in chat." },
        { Icon: MessageSquare, title: "Multilingual",          desc: "Speak Arabic, French, Spanish, Hindi, Mandarin — any international buyer feels at home." },
      ]}
      sampleChats={[
        { q: "Looking for a 2-bed flat near Canary Wharf, budget £2,300/month, dog-friendly.",
          a: "I have 4 matches. Top pick: 14 Mariners Court, 2-bed, £2,250pcm, pets considered, 6 min to South Quay station. Want photos + a viewing this week?" },
        { q: "How are the schools in that area?",
          a: "Mariners Court is in Tower Hamlets. Closest primary: Cyril Jackson (Ofsted Good, 0.4 mi). Closest secondary: Mulberry Stepney Green (Ofsted Outstanding, 1.1 mi). Catchment depends on yearly intake." },
        { q: "Can I see it tomorrow at 6pm?",
          a: "Yes — booked with Sarah at 18:00. I'll send confirmation and directions to your email and SMS. Postcode for parking: E14 9BS." },
      ]}
      sources={[
        "Your CRM (Reapit, Jupix, Alto, custom)",
        "Live listings feed (Rightmove / Zoopla / OnTheMarket)",
        "Agent calendars (Google / Outlook)",
        "Area data, schools, transport",
      ]}
      integrations={[
        "Reapit", "Jupix", "Alto", "PropertyFile", "Google Calendar", "Outlook",
        "WhatsApp Business", "Twilio SMS", "HubSpot", "Salesforce", "Zapier", "Custom REST",
      ]}
      metrics={[
        { value: "<2 min", label: "Average lead response time" },
        { value: "+47%",   label: "Viewings booked from chat" },
        { value: "24/7",   label: "Always-on coverage" },
        { value: "12+",    label: "CRM integrations" },
      ]}
      faqs={[
        { q: "Can it really read my live listings?",
          a: "Yes — via API or a portal scrape. New listings appear in the agent within minutes; sold/let listings drop off automatically." },
        { q: "Will it qualify low-quality leads out?",
          a: "Yes. You define the qualifying questions (budget, timeline, finance) and the agent applies them gently before booking a viewing." },
        { q: "Can it speak to international buyers?",
          a: "20+ languages live, including Arabic, Mandarin, French, German, Spanish and Hindi — common in international property markets." },
        { q: "Does it work for letting agents too?",
          a: "Absolutely. Templates exist for both sales and lettings, including referencing, deposit, and right-to-rent flows." },
      ]}
    />
  );
}
